/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import {
  AriaTabListProps,
  AriaTabPanelProps,
  useTab,
  useTabList,
  useTabPanel,
} from "react-aria";
import {
  Item as AriaItem,
  Node,
  TabListState,
  useTabListState,
} from "react-stately";
import { Box } from "../box";
import { useTheme } from "../theme";
import { StyleProps } from "../../utils/types";
import { cx } from "../../utils/dom";

export interface TabsProps extends AriaTabListProps<any>, StyleProps {
  orientation?: "horizontal" | "vertical";
}

export function Tabs({ className, style, ...props }: TabsProps) {
  const state = useTabListState(props);
  const tabRef = React.useRef<HTMLDivElement>(null);

  const [indicatorStyles, setIndicatorStyles] = React.useState({
    width: 0,
    left: 0,
  });

  const indicatorRef = React.useRef(null);

  const { tabListProps } = useTabList(props, state, tabRef);

  React.useEffect(() => {
    const selected: any = tabRef?.current?.querySelector(
      '.p-tab[aria-selected="true"]'
    );

    setIndicatorStyles({
      width: selected?.offsetWidth,
      left: selected?.offsetLeft,
    });
  }, [state.selectedKey]);

  const theme = useTheme();

  return (
    <Box
      style={style}
      data-color={theme?.color ?? "violet"}
      position={"relative"}
      className={cx("p-tabs", className)}
    >
      <div {...tabListProps} ref={tabRef} className="p-tab-list">
        <Box
          className="indicator"
          style={{
            left: indicatorStyles?.left,
            bottom: 0,
            background: "var(--color-10)",
            height: 2,
            marginBottom: -1,
            width: indicatorStyles?.width,
            transition: "all 0.2s ease",
          }}
          ref={indicatorRef}
          position={"absolute"}
        ></Box>
        {[...state.collection].map((item) => (
          <Tab
            orientation={props.orientation}
            key={item.key}
            item={item as any}
            state={state}
          ></Tab>
        ))}
      </div>
      <TabPanel key={state?.selectedItem?.key} state={state} />
    </Box>
  );
}

type TabProps = {
  item: Node<unknown>;
  state: TabListState<unknown>;
  orientation?: "horizontal" | "vertical";
};

function Tab({ item, state }: TabProps) {
  const { key, rendered } = item;
  const ref = React.useRef(null);

  const { tabProps, isDisabled, isSelected } = useTab({ key }, state, ref);

  return (
    <div
      id={`tab-${key}`}
      className="p-tab"
      ref={ref}
      data-disabled={isDisabled}
      data-selected={isSelected}
      {...tabProps}
    >
      {rendered}
    </div>
  );
}

type TabPanelProps = AriaTabPanelProps & {
  state: TabListState<unknown>;
};

function TabPanel({ state, ...props }: TabPanelProps) {
  const ref = React.useRef(null);
  const { tabPanelProps } = useTabPanel(props, state, ref);

  return (
    <div {...tabPanelProps} ref={ref}>
      {state.selectedItem?.props.children}
    </div>
  );
}

export const Item = AriaItem;
